import { useState } from 'react';

export default function Footer() {
  const [formData, setFormData] = useState({
    email: '',
    company: '',
    services: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6hgle057726vudt7u4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ email: '', company: '', services: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-800 text-white">
      {/* Sample Email Form Section */}
      <div id="sample-form" className="border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-4">Get Your Free Sample Email Pack</h3>
              <p className="text-lg text-gray-300">
                See real examples of emails written specifically for fire protection companies.
              </p>
            </div>

            <form
              id="sample-email-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-gray-900 p-8 rounded-lg"
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="sample-email" className="block text-sm font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="sample-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm text-white"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="sample-company" className="block text-sm font-semibold mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="sample-company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm text-white"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="sample-services" className="block text-sm font-semibold mb-2">
                    What services do you provide? *
                  </label>
                  <input
                    type="text"
                    id="sample-services"
                    name="services"
                    required
                    value={formData.services}
                    onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm text-white"
                    placeholder="e.g., Fire sprinkler inspection, alarm monitoring"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Me Sample Emails'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-900/50 border border-green-700 text-green-200 px-4 py-3 rounded-md text-sm">
                    Got it — we received your request. We'll send your sample email pack within 1 business day.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-md text-sm">
                    Something went wrong. Please try again.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/55f58ff0-c5af-4885-85a2-2e0c07bea16a.png"
              alt="Fireline Email Co."
              className="h-10 w-auto mb-4"
            />
            <p className="text-gray-400 leading-relaxed">
              Daily emails for fire protection and life safety companies. Building trust through consistent, intelligent communication.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Daily Email Writing</li>
              <li>Compliance Content</li>
              <li>Trust-Building Campaigns</li>
              <li>Email Strategy</li>
              <li>List Management</li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Industries We Serve</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Sprinkler Contractors</li>
              <li>Fire Alarm Companies</li>
              <li>Extinguisher Services</li>
              <li>Life-Safety Integrators</li>
              <li>Industrial Safety</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Fireline Email Co. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}